// Admin calls are same-origin (herdsafe.ai/api/admin/* -> Pages Function proxy ->
// api.herdsafe.ai) because the Cloudflare Access cookie is per-hostname. Public
// endpoints elsewhere keep using REACT_APP_BACKEND_URL. For local dev against a
// separate backend, set REACT_APP_ADMIN_API_BASE (e.g. http://localhost:8001/api).
const BASE = process.env.REACT_APP_ADMIN_API_BASE || "/api";

// When the Cloudflare Access session has expired, Access answers an XHR with a redirect to its login page:
// the browser reports that as a network error (cross-origin redirect), an opaque redirect, or an HTML page
// instead of JSON. Those errors carry `authBounce = true`: the fix is a top-level navigation, not a retry.
const ADMIN_TIMEOUT_MS = Number(process.env.REACT_APP_ADMIN_TIMEOUT_MS || 20000);
const ADMIN_TIMEOUT_MESSAGE = `The HerdSafe API did not answer in ${Math.round(ADMIN_TIMEOUT_MS / 1000)} s. It may be restarting. Try again in a minute.`;

function bounceError(message) {
  const e = new Error(message);
  e.authBounce = true;
  return e;
}

function timeoutError(message = ADMIN_TIMEOUT_MESSAGE) {
  const e = new Error(message);
  e.timeout = true;
  e.backendDown = true;
  return e;
}

export const isAuthBounce = (err) => !!(err && err.authBounce);
export const isBackendDown = (err) => !!(err && (err.timeout || err.backendDown || (typeof err.status === "number" && err.status >= 500)));

async function req(path, { method = "GET", body = null, headers = {} } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ADMIN_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (ex) {
    clearTimeout(timer);
    if (ex && ex.name === "AbortError") throw timeoutError();
    throw bounceError(`network error (${ex && ex.message ? ex.message : "request blocked"})`);
  }
  clearTimeout(timer);
  if (res.type === "opaqueredirect") throw bounceError("redirected to sign-in");
  const requestId = (res.headers && res.headers.get("x-request-id")) || "";
  let data = null;
  try { data = await res.json(); } catch (_) { /* no body */ }
  if (res.ok && data === null && res.redirected) throw bounceError("redirected to sign-in");
  if (!res.ok) {
    const err = new Error((data && data.detail) || `HTTP ${res.status}`);
    err.status = res.status;
    err.requestId = requestId;
    if ([502, 503, 504, 524].includes(res.status)) err.backendDown = true;
    throw err;
  }
  return { data, requestId };
}

export const api = {
  // Auth
  login: (password) => req("/admin/auth/login", { method: "POST", body: { password } }),
  logout: () => req("/admin/auth/logout", { method: "POST" }),
  me: () => req("/admin/auth/me"),
  authMode: async () => {
    const r = await req("/admin/auth/mode");
    if (!r.data || !r.data.mode) throw bounceError("no auth mode in the response (sign-in page?)");
    return r;
  },

  // Dashboard / review / items
  dashboard: () => req("/admin/dashboard"),
  reviewQueue: (qs) => req(`/admin/review-queue${qs ? `?${qs}` : ""}`),
  itemReview: (id) => req(`/admin/items/${id}/review`),
  humanVerdict: (id, body) => req(`/admin/items/${id}/human-verdict`, { method: "POST", body }),
  items: (qs) => req(`/admin/items?${qs || ""}`),
  itemDetail: (id) => req(`/admin/items/${id}`),
  itemEvidence: (id, { full, snapshotId } = {}) => {
    const q = new URLSearchParams();
    if (full) q.set("full", "true");
    if (snapshotId) q.set("snapshot_id", snapshotId);
    return req(`/admin/items/${id}/evidence?${q.toString()}`);
  },
  reviewNext: (exclude, cleared) => {
    const p = new URLSearchParams();
    if (exclude && exclude.length) p.set("exclude", exclude.join(","));
    if (cleared && cleared !== "hide") p.set("cleared", cleared);
    const qs = p.toString();
    return req(`/admin/review/next${qs ? `?${qs}` : ""}`);
  },
  ruleFeedback: (body) => req("/admin/review/rule-feedback", { method: "POST", body }),
  rescoreRules: (body) => req("/admin/rescore-rules", { method: "POST", body: body || {} }),
  exportReviewData: async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ADMIN_TIMEOUT_MS);
    try {
      const res = await fetch(`${BASE}/admin/export/review-data`, {
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (res.type === "opaqueredirect") throw bounceError("redirected to sign-in");
      if (!res.ok) {
        const e = new Error(`HTTP ${res.status}`);
        e.status = res.status;
        if ([502, 503, 504, 524].includes(res.status)) e.backendDown = true;
        throw e;
      }
      return res.blob();
    } catch (ex) {
      if (ex && ex.name === "AbortError") throw timeoutError();
      throw ex;
    } finally {
      clearTimeout(timer);
    }
  },
  itemGroups: (by, qs) => req(`/admin/items-groups?by=${by}${qs ? `&${qs}` : ""}`),
  itemBrief: (id) => req(`/admin/items/${id}/brief`),
  bulkLabel: (body) => req("/admin/items/bulk-label", { method: "POST", body }),
  bulkRuleFeedback: (body) => req("/admin/items/bulk-rule-feedback", { method: "POST", body }),
  researchStats: () => req("/admin/stats/research"),
  llmTest: () => req("/admin/llm/test", { method: "POST" }),
  rescoreEstimate: (body) => req("/admin/rescore/estimate", { method: "POST", body }),
  sources: () => req("/admin/sources"),
  sourcesOverview: (days) => req(`/admin/sources/overview?days=${days || 3}`),
  patchSource: (id, patch) => req(`/admin/sources/${id}`, { method: "PATCH", body: patch }),
  runSource: (id) => req(`/admin/sources/${id}/run`, { method: "POST" }),
  refetchMissing: (id) => req(`/admin/sources/${id}/refetch-missing`, { method: "POST" }),
  createSource: (body) => req("/admin/sources", { method: "POST", body }),
  deleteSource: (id, purge) => req(`/admin/sources/${id}?purge_items=${purge ? "true" : "false"}`, { method: "DELETE" }),
  deleteDemoSources: () => req("/admin/sources/delete-demo", { method: "POST" }),
  audit: (qs) => req(`/admin/audit?${qs || ""}`),
  manualItem: (body) => req("/admin/items/manual", { method: "POST", body }),

  // Phase 5 — disclosures
  itemDisclosures: (id) => req(`/admin/items/${id}/disclosures`),
  createDisclosure: (id, body) => req(`/admin/items/${id}/disclosures`, { method: "POST", body }),
  patchDisclosure: (id, body) => req(`/admin/disclosures/${id}`, { method: "PATCH", body }),
  listDisclosures: (qs) => req(`/admin/disclosures?${qs || ""}`),

  // Phase 5 — references
  itemReferences: (id) => req(`/admin/items/${id}/references`),
  createReference: (id, body) => req(`/admin/items/${id}/references`, { method: "POST", body }),
  deleteReference: (id) => req(`/admin/references/${id}`, { method: "DELETE" }),

  // Phase 5 — appeals
  listAppeals: (qs) => req(`/admin/appeals?${qs || ""}`),
  patchAppeal: (id, body) => req(`/admin/appeals/${id}`, { method: "PATCH", body }),

  // Phase 5 — submissions
  listSubmissions: (qs) => req(`/admin/submissions?${qs || ""}`),
  getSubmission: (id) => req(`/admin/submissions/${id}`),
  patchSubmission: (id, body) => req(`/admin/submissions/${id}`, { method: "PATCH", body }),

  // Phase 5 — re-score
  rescore: (body) => req("/admin/rescore", { method: "POST", body: body || { since: "all" } }),
  rescoreStatus: (id) => req(`/admin/rescore/${id}`),
  rescoreJobs: () => req("/admin/rescore-jobs"),
  rescorePause: (id) => req(`/admin/rescore/${id}/pause`, { method: "POST" }),
  rescoreResume: (id) => req(`/admin/rescore/${id}/resume`, { method: "POST" }),

  // Phase 6 — reports
  listReports: (qs) => req(`/admin/reports?${qs || ""}`),
  getReport: (id) => req(`/admin/reports/${id}`),
  generateReport: (period) => req("/admin/reports/generate", { method: "POST", body: { period } }),
  patchReport: (id, markdown) => req(`/admin/reports/${id}`, { method: "PATCH", body: { markdown } }),
  publishReport: (id) => req(`/admin/reports/${id}/publish`, { method: "POST" }),
  unpublishReport: (id) => req(`/admin/reports/${id}/unpublish`, { method: "POST" }),

  // Review history
  myReviews: (q) => req(`/admin/reviews/mine${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  editReview: (id, body) => req(`/admin/reviews/${id}`, { method: "PATCH", body }),
  undoReview: (id) => req(`/admin/reviews/${id}/undo`, { method: "POST" }),

  // Automated confirmation
  autoConfirm: (days) => req(`/admin/auto-confirm?days=${days || 30}`),
  undoAutoConfirm: (id) => req(`/admin/auto-confirm/${id}/undo`, { method: "POST" }),

  // Code evidence
  codeEvidence: (itemId) => req(`/admin/items/${itemId}/code-evidence`),
  queuePackageScan: (itemId) => req(`/admin/items/${itemId}/queue-package-scan`, { method: "POST" }),

  // Server profiles
  itemProfile: (itemId) => req(`/admin/items/${itemId}/profile`),
  createProfile: (itemId) => req(`/admin/items/${itemId}/profile`, { method: "POST" }),
  listProfiles: () => req("/admin/profiles"),
  editProfile: (id, body) => req(`/admin/profiles/${id}`, { method: "PATCH", body }),
  publishProfile: (id) => req(`/admin/profiles/${id}/publish`, { method: "POST" }),
  unpublishProfile: (id) => req(`/admin/profiles/${id}/unpublish`, { method: "POST" }),

  // Moderation / privacy / blocklist
  moderationList: (kind, view) => req(`/admin/moderation/${kind}?view=${view || "active"}`),
  moderationAction: (kind, action, body) => req(`/admin/moderation/${kind}/${action}`, { method: "POST", body }),
  blockSubmitter: (kind, id, body) => req(`/admin/moderation/${kind}/${id}/block`, { method: "POST", body }),
  blocklist: () => req("/admin/blocklist"),
  removeBlock: (id) => req(`/admin/blocklist/${id}`, { method: "DELETE" }),
  privacyFind: (email) => req("/admin/privacy/email/find", { method: "POST", body: { email } }),
  privacyDelete: (email, confirm) => req("/admin/privacy/email/delete", { method: "POST", body: { email, confirm } }),
  hideItem: (itemId, hidden, reason) => req(`/admin/items/${itemId}/hide-from-public`, { method: "POST", body: { hidden, reason } }),
  deleteItem: (itemId, confirm, reason) => req(`/admin/items/${itemId}/delete`, { method: "POST", body: { confirm, reason } }),
  upstreamDrafts: (itemId) => req(`/admin/items/${itemId}/upstream-report`, { method: "POST" }),
  upstreamSent: (itemId, authority, note) => req(`/admin/items/${itemId}/upstream-report/sent`, { method: "POST", body: { authority, note } }),
  secondOpinions: (itemId) => req(`/admin/items/${itemId}/second-opinions`),
  reaskAi: (itemId) => req(`/admin/items/${itemId}/reask-ai`, { method: "POST" }),
  askSecondOpinion: (itemId, snapshotId) => req(`/admin/items/${itemId}/second-opinion`, { method: "POST", body: snapshotId ? { snapshot_id: snapshotId } : {} }),
  developers: () => req("/admin/developers"),
  runWatchAlerts: () => req("/admin/watch-alerts/run", { method: "POST" }),

  // Early-access waitlist
  waitlist: (qs) => req(`/admin/waitlist${qs ? `?${qs}` : ""}`),
  patchWaitlist: (id, body) => req(`/admin/waitlist/${id}`, { method: "PATCH", body }),

  // Phase 7 — API keys
  listApiKeys: () => req("/admin/api-keys"),
  createApiKey: (body) => req("/admin/api-keys", { method: "POST", body }),
  revokeApiKey: (id) => req(`/admin/api-keys/${id}/revoke`, { method: "POST" }),

  // Phase 9 — signing keys
  listSigningKeys: () => req("/admin/signing-keys"),
  rotateSigningKey: () => req("/admin/signing-keys/rotate", { method: "POST" }),
};

// Public (no credentials) endpoints — used by /appeal and /report. These go straight to the API
// host (REACT_APP_BACKEND_URL), NOT the same-origin admin proxy.
const PUBLIC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function publicRequest(path, options) {
  const res = await fetch(`${PUBLIC_BASE}${path}`, options);
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    throw new Error((data && data.detail) || `HTTP ${res.status}`);
  }
  return data;
}

export const publicGet = (path) => publicRequest(path, {});

// POST without a body (query-string parameters only).
export const publicRequestPost = (path) => publicRequest(path, { method: "POST" });

export const publicPost = (path, body) => publicRequest(path, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});


// ─── developer session (magic link) ──────────────────────────────────────────
// The session token is a bearer token in localStorage (the API is on another origin, so no cookie). Only its hash
// is stored server-side; it expires after 30 days and "Sign out" deletes it on both sides.
const DEV_KEY = "hs-dev-session";

export function devSession() {
  try { return window.localStorage.getItem(DEV_KEY) || ""; } catch (_) { return ""; }
}

export function setDevSession(tok) {
  try { if (tok) window.localStorage.setItem(DEV_KEY, tok); else window.localStorage.removeItem(DEV_KEY); } catch (_) { /* storage blocked */ }
}

export async function devRequest(path, { method = "GET", body } = {}) {
  const headers = { Authorization: `Bearer ${devSession()}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${PUBLIC_BASE}${path}`, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    const e = new Error((data && data.detail) || `HTTP ${res.status}`);
    e.status = res.status;
    if (res.status === 401) setDevSession("");
    throw e;
  }
  return data;
}


// ─── top-level navigation helpers (Cloudflare Access) ─────────────────────────
const BOUNCE_KEY = "hs-admin-bounce-at";
const BOUNCE_WINDOW_MS = 2 * 60 * 1000;

/** One full-page navigation to /admin so Cloudflare Access can show its sign-in. Returns false (and does
 *  nothing) if we already bounced in the last two minutes: that is a loop, and the caller shows an error. */
export function bounceToAdmin(win = window) {
  let last = 0;
  try { last = Number(win.sessionStorage.getItem(BOUNCE_KEY) || 0); } catch (_) { /* storage blocked */ }
  if (Date.now() - last < BOUNCE_WINDOW_MS) return false;
  try { win.sessionStorage.setItem(BOUNCE_KEY, String(Date.now())); } catch (_) { /* storage blocked */ }
  win.location.assign("/admin");
  return true;
}

export function clearBounce(win = window) {
  try { win.sessionStorage.removeItem(BOUNCE_KEY); } catch (_) { /* storage blocked */ }
}

// Cloudflare Access logout on the APPLICATION domain: per Cloudflare's docs both this and the team-domain
// URL revoke the session across all Access applications; the app-domain one also removes this hostname's
// cookie immediately. The next visit therefore asks for a new one-time PIN.
export const CF_ACCESS_LOGOUT_URL = process.env.REACT_APP_CF_ACCESS_LOGOUT_URL || "/cdn-cgi/access/logout";
